import { usePathHelper } from '@/hooks/usePathHelper'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'
import { useFormContext } from '@/lib/form-contexts'
import _get from 'lodash/get'
import { Link, useParams } from '@tanstack/react-router'
import {
  addTopMessageBarPageLink,
  hasTopMessageBarLink,
  PAGE_ANNOUNCEMENT_LINK_OPTIONS,
  removeTopMessageBarPageLink,
  TOP_MESSAGE_BAR_ENABLED_FIELD,
  TOP_MESSAGE_BAR_NAV_ITEMS_FIELD,
} from './topMessageBarLinks'
import { WEBSITE_EDITOR_TARGET_SECTION_KEY } from './websiteEditorTargetSection'

const HEADER_SECTION_ID = 'header'

const PageAnnouncementBarLinkToggle = ({ pageKey }) => {
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()
  const path = usePathHelper()
  const { orgId } = useParams({ strict: false })
  const option = PAGE_ANNOUNCEMENT_LINK_OPTIONS[pageKey]

  if (!option) {
    return null
  }

  const toggleId = `top-message-bar-link-${pageKey}`
  const headerNavigationLink = path.builder.index().using({ orgId }).search({
    section: 'brand-layout',
  })

  const handleHeaderNavigationClick = () => {
    if (typeof window === 'undefined') {
      return
    }

    window.sessionStorage.setItem(WEBSITE_EDITOR_TARGET_SECTION_KEY, HEADER_SECTION_ID)
  }

  const handleCheckedChange = (isChecked) => {
    const currentItems = form.getFieldValue(TOP_MESSAGE_BAR_NAV_ITEMS_FIELD)

    if (isChecked) {
      form.setFieldValue(
        TOP_MESSAGE_BAR_NAV_ITEMS_FIELD,
        addTopMessageBarPageLink(currentItems, option),
        { touch: true }
      )
      form.setFieldValue(TOP_MESSAGE_BAR_ENABLED_FIELD, true, { touch: true })
      return
    }

    form.setFieldValue(
      TOP_MESSAGE_BAR_NAV_ITEMS_FIELD,
      removeTopMessageBarPageLink(currentItems, option.href),
      { touch: true }
    )
  }

  return (
    <form.Subscribe selector={(state) => _get(state.values, TOP_MESSAGE_BAR_NAV_ITEMS_FIELD)}>
      {(navItems) => {
        const isChecked = hasTopMessageBarLink(navItems, option.href)

        return (
          <div className="rounded-xl bg-muted/30 px-3 py-3">
            <div className="flex items-center gap-3">
              <Switch
                id={toggleId}
                checked={isChecked}
                onCheckedChange={handleCheckedChange}
                disabled={isReadOnly}
              />
              <div className="min-w-0 space-y-1">
                <Label htmlFor={toggleId}>Show {option.label} in the announcement bar</Label>
                <Typography variant="body" className="block sm:text-sm/6">
                  Adds a quick link on the right side of the bar. You can rename or reorder it in{' '}
                  <Typography as="span" variant="body" tone="interaction" interaction="link">
                    <Link {...headerNavigationLink.link} onClick={handleHeaderNavigationClick}>
                      Header & Navigation
                    </Link>
                  </Typography>{' '}
                  settings.
                </Typography>
              </div>
            </div>
          </div>
        )
      }}
    </form.Subscribe>
  )
}

export default PageAnnouncementBarLinkToggle
